import jwtDecode from 'jwt-decode';
import { Event } from '~/types/Event';
import { OpenIdTokens } from '~/types/OpenIdTokens';

export function getTokens(eventAccessCode: string) {
	const tokensKey = `tokens_${eventAccessCode}`;
	const tokens = localStorage.getItem(tokensKey);
	if (tokens) {
		return JSON.parse(tokens);
	}

	return undefined;
}

export function cacheTokens(eventAccessCode: string, tokens: OpenIdTokens) {
	const tokensKey = `tokens_${eventAccessCode}`;
	localStorage.setItem(tokensKey, JSON.stringify(tokens));
}

export function cacheEvent(eventAccessCode: string, event: Event) {
	const eventsKey = `events_${eventAccessCode}`;
	localStorage.setItem(eventsKey, JSON.stringify(event));
}

export function getIdToken(eventAccessCode: string) {
	const tokensKey = `tokens_${eventAccessCode}`;
	if (localStorage.getItem(tokensKey)) {
		return JSON.parse(localStorage.getItem(tokensKey) || '{}')?.id_token;
	}

	return null;
}

export function getCachedEvent(eventAccessCode: string) {
	const eventsKey = `events_${eventAccessCode}`;
	if (localStorage.getItem(eventsKey)) {
		return JSON.parse(localStorage.getItem(eventsKey) || '{}');
	}

	return null;
}

export function getCachedEvents(): [key: string, value: Event][] {
	return Object.entries(localStorage)
		.filter(([key]) => key.startsWith('events_'))
		.map(([key, value]) => [key.replace('events_', ''), JSON.parse(value)]);
}

export function getAccessToken(eventAccessCode: string) {
	const tokensKey = `tokens_${eventAccessCode}`;
	if (localStorage.getItem(tokensKey)) {
		return JSON.parse(localStorage.getItem(tokensKey) || '{}')?.access_token;
	}

	return null;
}

export function getDecodedToken(eventAccessCode: string) {
	const idToken = getIdToken(eventAccessCode);
	return idToken && jwtDecode(idToken);
}

export function getEventId(eventAccessCode: string): string | undefined {
	const token = getDecodedToken(eventAccessCode);
	if (token) {
		return token.eventId;
	}

	return undefined;
}

export function removeCachedEvent(eventAccessCode: string): void {
	localStorage.removeItem(`events_${eventAccessCode}`);
	localStorage.removeItem(`tokens_${eventAccessCode}`);
}

export function setScannerUserToken(eventAccessCode: string, scannerName: string) {
	const scannerUserKey = `scannerUser_${eventAccessCode}`;
	localStorage.setItem(scannerUserKey, scannerName );
}

export function getScannerUserToken(eventAccessCode: string) {
	const tokensKey = `scannerUser_${eventAccessCode}`;
	if (localStorage.getItem(tokensKey)) {
		return localStorage.getItem(tokensKey);
	}
	return null;
}

const DEVICE_ID_KEY = 'tappin_device_id';

export function getDeviceId(): string {
	let deviceId = localStorage.getItem(DEVICE_ID_KEY);
	if (!deviceId) {
		deviceId = crypto.randomUUID();
		localStorage.setItem(DEVICE_ID_KEY, deviceId);
	}
	return deviceId;
}

/* ------------------------------------------------------------------
 * Sponsor lead-capture token cache. Distinct key from `tokens_<accessCode>`
 * so the ticket-scanner and sponsor-scanner flows can coexist on the
 * same device for the same event without trampling each other.
 * ------------------------------------------------------------------ */

function sponsorTokensKey(sponsorId: string) {
	return `tokens_sponsor_${sponsorId}`;
}

export function getSponsorTokens(sponsorId: string): OpenIdTokens | undefined {
	const raw = localStorage.getItem(sponsorTokensKey(sponsorId));
	return raw ? JSON.parse(raw) : undefined;
}

export function cacheSponsorTokens(sponsorId: string, tokens: OpenIdTokens) {
	localStorage.setItem(sponsorTokensKey(sponsorId), JSON.stringify(tokens));
}

export function getSponsorAccessToken(sponsorId: string): string | null {
	const tokens = getSponsorTokens(sponsorId);
	return tokens?.access_token ?? null;
}

export function clearSponsorTokens(sponsorId: string) {
	localStorage.removeItem(sponsorTokensKey(sponsorId));
}

/** Selected representative — persisted per-sponsor in sessionStorage so a refresh
 * mid-session doesn't kick the rep back to the picker, but it does clear when the
 * browser is closed (rotation safety at multi-rep booths). */
export function getSelectedRepresentative(sponsorId: string): string | null {
	return sessionStorage.getItem(`sponsor_scan_rep_${sponsorId}`);
}

export function setSelectedRepresentative(sponsorId: string, repId: string | null) {
	const key = `sponsor_scan_rep_${sponsorId}`;
	if (repId) {
		sessionStorage.setItem(key, repId);
	} else {
		sessionStorage.removeItem(key);
	}
}